import React from "react";
import styled from "@emotion/styled";
import { Text } from "styles/utility-components";

import Modal from "components/ui/modal/Modal";
import StyledListWrapper from "../../lib/core_components/StyledListWrapper";
import { TextLink } from "../actions/Links";

const HELP_EDITING_YOUR_FEED_URL = "https://help.stotles.com/optimise-your-signal-settings";

type Props = {
  onClose(): void;
  isOpen: boolean;
};

export function TextSearchTipsModal({ onClose, isOpen }: Props): React.ReactElement {
  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      width="720px"
      padding={24}
    >
      <Text h2>Searching for keywords</Text>
      <SubtitleBlock>
        <Text h3>Advanced keyword search options</Text>
        <Intro>
          See{" "}
          <TextLink to={HELP_EDITING_YOUR_FEED_URL} targetType="external" showIcon={false}>
            <b>our tips</b>
          </TextLink>{" "}
          on how to create a feed that is hyper-relevant to what you are looking for.
        </Intro>
      </SubtitleBlock>
      <StyledListWrapper>
        <TipsSection>
          <p>A normal keyword entry will include other words that share the same root word.</p>
          <ul>
            <li>
              E.g., <em>consulting</em> will match <em>consultant</em> and <em>consultation</em>
            </li>
            <li>
              E.g., <em>facility management</em> will match <em>facilities management</em>
            </li>
          </ul>
        </TipsSection>
        <TipsSection>
          <p>To search for an exact match, use quotations. This is not case sensitive.</p>
          <ul>
            <li>
              E.g., <em>"consulting"</em> will <u>not</u> match <em>consultant</em> or{" "}
              <em>consultation</em>
            </li>
            <li>
              E.g., <em>"facility management"</em> will <u>not</u> match{" "}
              <em>facilities management</em>
            </li>
            <li>
              E.g., <em>"app"</em> will <u>not</u> match <em>application</em>. The match considers
              the whole word
            </li>
          </ul>
        </TipsSection>
        <TipsSection>
          <p>
            To better identify acronyms, keywords typed in ALL CAPS will only match keywords that
            are also fully capitalised. Any other capitalisation will search with no case
            sensitivity.
          </p>
          <ul>
            <li>
              E.g., <em>CHECK</em> (the cyber security scheme) will <u>not</u> match <em>check</em>{" "}
              (the verb)
            </li>
            <li>
              E.g., <em>SAAS</em> will <u>not</u> match <em>SaaS</em>, but <em>saas</em> will
            </li>
          </ul>
        </TipsSection>
        <TipsSection>
          <p>To narrow down certain keyword results, combine them with the AND operator.</p>
          <ul>
            <li>
              E.g., <em>software AND design</em> would find more relevant results than if you typed{" "}
              <em>software</em> and <em>design</em> separately
            </li>
          </ul>
        </TipsSection>
        <TipsSection>
          <p>To add several keywords at once, paste a comma- or semicolon-separated list.</p>
          <ul>
            <li>
              E.g., pasting <em>consulting, facilities management, "cyber security"</em> adds three
              keywords in one go
            </li>
          </ul>
        </TipsSection>
      </StyledListWrapper>
    </Modal>
  );
}

const SubtitleBlock = styled.div({
  marginTop: 16,
});

const Intro = styled.em(({ theme }) => ({
  display: "block",
  marginTop: 4,
  marginBottom: 12,
  color: theme.colors.sysTextDefault,
}));

const TipsSection = styled.div(({ theme }) => ({
  marginBottom: 12,
  "&:last-of-type": {
    marginBottom: 0,
  },
  "& p": {
    margin: 0,
    color: theme.colors.sysTextDefault,
  },
  "& ul": {
    marginTop: 4,
  },
  "& li": {
    color: theme.colors.sysTextDefault,
  },
  "& li + li": {
    marginTop: 2,
  },
}));
